/**
 * Radio Chatter Keyword Tagger — client-side severity classification.
 *
 * Instant keyword matching for military radio terminology.
 * No network calls — runs entirely in the browser.
 */

import type { RadioSeverity } from '../types'

const CRITICAL_KEYWORDS = [
  'MAYDAY',
  'EMERGENCY',
  'BINGO',
  'WINCHESTER',
  'MISSION ABORT',
  'ABORT',
  'EJECT',
  'BAILOUT',
  'TROOPS IN CONTACT',
  'TIC',
  'CSAR',
  'MAN DOWN',
  'BROKEN ARROW',
]

const WARNING_KEYWORDS = [
  'JOKER',
  'RTB',
  'DIVERT',
  'CANCEL',
  'MINUS',
  'DEGRADED',
  'WOUNDED',
  'MEDEVAC',
  'SCRAMBLE',
  'CONTACT',
  'HOSTILE',
  'SPLASH',
  'BUSTER',
  'DEFENSIVE',
]

const INFO_KEYWORDS = [
  'CHECK IN',
  'CHECKING IN',
  'AS FRAGGED',
  'ON STATION',
  'FENCE IN',
  'FENCE OUT',
  'PUSH',
  'CLEARED HOT',
  'TANKER',
  'BOOM',
  'OFFLOAD',
  'COMPLETE',
  'ROGER',
  'COPY',
  'WILCO',
]

interface TagResult {
  severity: RadioSeverity
  tags: string[]
}

export function tagTranscript(text: string): TagResult {
  const upper = text.toUpperCase()
  const tags: string[] = []
  let severity: RadioSeverity = 'ROUTINE'

  for (const kw of CRITICAL_KEYWORDS) {
    if (upper.includes(kw)) {
      tags.push(kw)
      severity = 'CRITICAL'
    }
  }

  if (severity !== 'CRITICAL') {
    for (const kw of WARNING_KEYWORDS) {
      if (upper.includes(kw)) {
        tags.push(kw)
        if (severity !== 'WARNING') severity = 'WARNING'
      }
    }
  }

  if (severity === 'ROUTINE') {
    for (const kw of INFO_KEYWORDS) {
      if (upper.includes(kw)) {
        tags.push(kw)
        severity = 'INFO'
      }
    }
  }

  return { severity, tags }
}

export const SEVERITY_COLORS: Record<RadioSeverity, { border: string; bg: string; text: string; dot: string }> = {
  CRITICAL: { border: '#ef4444', bg: 'rgba(239, 68, 68, 0.15)', text: '#fca5a5', dot: '#ef4444' },
  WARNING: { border: '#f59e0b', bg: 'rgba(245, 158, 11, 0.15)', text: '#fcd34d', dot: '#f59e0b' },
  INFO: { border: '#3b82f6', bg: 'rgba(59, 130, 246, 0.15)', text: '#93c5fd', dot: '#3b82f6' },
  ROUTINE: { border: '#6b7280', bg: 'rgba(107, 114, 128, 0.1)', text: '#9ca3af', dot: '#6b7280' },
}
